import {
  buildTestCounts,
  type TestStatusKey,
} from '@/features/dashboard/components/dashboardStatusDataHelpers';
import { TestExecutionsTable } from '@/features/executions/components/TestExecutionsTable';
import { queryTestExecutionStatusSummary } from '@/features/executions/data';
import { EntityTrendsPageShell } from '@/features/shared/components/EntityTrendsPageShell';
import { useEntityTrendsPage } from '@/features/shared/hooks/useEntityTrendsPage';
import { TestsTable } from '@/features/tests/components/TestsTable';
import { queryTestStatusSummary, queryTests } from '@/features/tests/data/tests';
import { testExecutionSummaryStatusOptions } from '@/shared/constants/statusOptions';
import { gridPaths, queryKeys } from '@/shared/hooks';
import type { GridQueryInput } from '@/ui/composites/grid/gridUtils';
import type {
  QueryResult,
  TestExecutionStatusSummaryDto,
  TestListItemDto,
  TestSummaryDto,
} from '@flowqa/shared';
import type { ReactElement } from 'react';

export const TestsPage = (): ReactElement => {
  const viewModel = useEntityTrendsPage<
    TestListItemDto,
    TestSummaryDto | TestExecutionStatusSummaryDto
  >({
    loadingMessage: 'Loading tests...',
    metricsScope: 'test',
    gridPath: gridPaths.tests,
    executionGridPath: gridPaths.testExecutions,
    entitySearchField: 'name',
    executionSearchField: 'testName',
    statusOptions: testExecutionSummaryStatusOptions.map((option) => ({
      value: option.value as Exclude<TestStatusKey, 'untested'>,
      label: option.label,
    })),
    summaryKey: (projectId, organisationId, filtersKey) =>
      queryKeys.testSummary(projectId, organisationId, filtersKey),
    executionSummaryKey: (projectId, organisationId, filtersKey) =>
      queryKeys.testExecutionSummary(projectId, organisationId, filtersKey),
    summaryPrefixKey: queryKeys.testSummaryPrefix,
    executionSummaryPrefixKey: queryKeys.testExecutionSummaryPrefix,
    queryLatestSummary: queryTestStatusSummary,
    queryExecutionSummary: queryTestExecutionStatusSummary,
    buildCounts: buildTestCounts,
    selectThresholds: (thresholds) => thresholds.tests,
    queryEntityTable: async (
      input: GridQueryInput,
      {
        selectedProjectId,
        selectedOrganisationId,
        rangeStartIso,
        rangeEndIso,
        previousRangeStartIso,
        previousRangeEndIso,
        deltasEnabled,
        tableDeltasEnabled,
      },
    ): Promise<QueryResult<TestListItemDto>> =>
      queryTests(selectedProjectId, selectedOrganisationId ?? undefined, {
        ...input,
        metricsStartDay: rangeStartIso ?? undefined,
        metricsEndDay: rangeEndIso ?? undefined,
        previousMetricsStartDay: previousRangeStartIso ?? undefined,
        previousMetricsEndDay: previousRangeEndIso ?? undefined,
        include: {
          suites: { ids: true, items: true, primary: true },
          latestExecution: true,
          metrics: true,
          metricDeltas: deltasEnabled && tableDeltasEnabled,
        },
      }),
  });

  return (
    <EntityTrendsPageShell
      viewModel={viewModel}
      title="Tests"
      addButtonLabel="Add Test"
      searchAriaLabel="Search tests"
      chartTabsAriaLabel="Tests chart tabs"
      pageTabsAriaLabel="Tests page tabs"
      entityTabLabel="Tests"
      chartEmptyStateTitle="No test executions found"
      entityTableNoRowsText="No tests found"
      executionTableNoRowsText="No test executions found"
      persistKey="tests-table"
      executionPersistKey="test-executions-table"
      renderEntityTable={({
        queryKey,
        queryFn,
        fixedFilters,
        showDeltas,
        persistKey,
        noRowsText,
      }) => (
        <TestsTable
          persistKey={persistKey}
          queryKey={queryKey}
          queryFn={queryFn}
          pageSizeOptions={[10]}
          initialPageSize={10}
          disablePageSizeSelect
          showDeltas={showDeltas}
          fixedFilters={fixedFilters}
          noRowsText={noRowsText}
        />
      )}
      renderExecutionsTable={({ fixedFilters, persistKey, noRowsText }) => (
        <TestExecutionsTable
          persistKey={persistKey}
          fixedFilters={fixedFilters}
          pageSizeOptions={[10]}
          initialPageSize={10}
          disablePageSizeSelect
          noRowsText={noRowsText}
        />
      )}
    />
  );
};
